import { DateTime } from 'luxon';
import { dateFormat as dateFormatOptions, timeFormat as timeFormatOptions } from 'shared/constants.json';
import { humanReadableDiffFromNow } from 'shared/lib/time';
import { isNullOrUndefined } from 'shared/lib/object/object';

const EXPLICIT_FORMATS = {
  [dateFormatOptions.monthDayYear]: 'MM/dd/yyyy',
  [dateFormatOptions.dayMonthYear]: 'dd/MM/yyyy',
  [dateFormatOptions.yearMonthDay]: 'yyyy/MM/dd',
};

// Accepted when a date is typed by hand. formatEditableDate must only ever
// render something this list can parse back.
export const INPUT_FORMATS = [
  'dd. MMM yyyy', 'd. MMM yyyy',
  'dd.MM.yyyy', 'd.M.yyyy', 'd.M.yy',
  'dd/MM/yyyy', 'd/M/yyyy',
  'MMM dd yyyy', 'MMM d yyyy', 'MMM d, yyyy', 'MMM dd, yyyy',
  'MMMM dd yyyy', 'MMMM d yyyy',
];

export const showsTime = (timeFormat) => !isNullOrUndefined(timeFormat) && timeFormat !== timeFormatOptions.hidden;

export const resolveShowTime = ({ includeTime, timeFormat, override }) => {
  if (typeof override === 'boolean') {
    return override;
  }

  return includeTime === true || showsTime(timeFormat);
};

export const parseDateInput = (value, dateTime = DateTime) => {
  if (value === null || typeof value === 'undefined' || `${value}`.trim() === '') {
    return null;
  }

  for (let i = 0; i < INPUT_FORMATS.length; i++) {
    const parsed = dateTime.fromFormat(value, INPUT_FORMATS[i]);
    if (parsed.invalid === null) {
      return parsed;
    }
  }

  return null;
};

const addLeadingZero = (n) => (n < 10 ? `0${n}` : `${n}`);

export const formatEditableDate = (date, language, monthShort, dateTime = DateTime) => {
  if (date === null || typeof date === 'undefined') {
    return '';
  }

  const d = dateTime.fromISO(date);
  if (language === 'de') {
    return `${addLeadingZero(d.day)}.${addLeadingZero(d.month)}.${d.year}`;
  }

  return `${monthShort(d.month)} ${d.day}, ${d.year}`;
};

const formatDatePart = (d, dateFormat) => {
  if (typeof EXPLICIT_FORMATS[dateFormat] !== 'undefined') {
    return d.toFormat(EXPLICIT_FORMATS[dateFormat]);
  }

  if (dateFormat === dateFormatOptions.full) {
    return d.toLocaleString(DateTime.DATE_FULL);
  }

  return d.toLocaleString(DateTime.DATE_SHORT);
};

export const dateFormatter = (dateFormat, timeFormat, locale, t) => (value) => {
  if (value === null || typeof value === 'undefined') {
    return '';
  }

  const d = DateTime.fromISO(value, { setZone: true }).setLocale(locale);
  if (d.invalid !== null) {
    return '';
  }

  if (dateFormat === dateFormatOptions.relative && typeof t === 'function') {
    const diff = humanReadableDiffFromNow(d);
    return t(diff.translationKey, diff);
  }

  const datePart = formatDatePart(d, dateFormat);
  if (!showsTime(timeFormat)) {
    return datePart;
  }

  return `${datePart} ${d.toFormat('HH:mm')}`;
};
